import type { useNavigate, useSearchParams } from 'react-router'
import type { QuickSearchCategoryView, QuickSearchView } from '@/components/QuickSearch/quickSearchRoutes'
import type { QuickSearchCategoryName, QuickSearchCategoryPage, QuickSearchResponse, QuickSearchResult } from '@/types/searchTypes'
import { useCallback } from 'react'
import { buildQuickSearchResultLocation } from '@/components/QuickSearch/quickSearchRoutes'
import {
  ANALYTICS_EVENTS,
  ANALYTICS_JOURNEY_CHANNELS,
  setPendingJourneyChannel,
  trackAnalyticsEvent,
} from '@/services/analytics'
import { getCategoryFromResultType } from '@/utils/quickSearch'

interface UseQuickSearchResultsPageActionsOptions {
  query: string
  season: string
  selectedView: QuickSearchView
  selectedCategory: QuickSearchCategoryName
  allCategoryResults?: QuickSearchResponse
  categoryData?: QuickSearchCategoryPage
  navigate: ReturnType<typeof useNavigate>
  setSearchParams: ReturnType<typeof useSearchParams>[1]
}

export function useQuickSearchResultsPageActions({
  query,
  season,
  selectedView,
  selectedCategory,
  allCategoryResults,
  categoryData,
  navigate,
  setSearchParams,
}: UseQuickSearchResultsPageActionsOptions) {
  const isAllView = selectedView === 'all'

  const handleFilterChange = (newFilter: QuickSearchView) => {
    let categoryTotal: number | undefined
    if (newFilter !== 'all') {
      if (isAllView) {
        categoryTotal = allCategoryResults?.categories[newFilter].total
      }
      else if (newFilter === selectedCategory) {
        categoryTotal = categoryData?.total
      }
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_VIEW_CHANGED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_results_page',
      season,
      query,
      query_length: query.length,
      previous_view: selectedView,
      selected_view: newFilter,
      category_total: categoryTotal,
      total_results: isAllView && allCategoryResults ? allCategoryResults.total : undefined,
    })

    if (newFilter === 'all') {
      setSearchParams({ query })
    }
    else {
      setSearchParams({ query, filter: newFilter })
    }
  }

  const handleSeeAll = (category: QuickSearchCategoryView) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_SEE_ALL_CLICKED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_results_page',
      season,
      query,
      query_length: query.length,
      selected_view: selectedView,
      target_view: category,
      category_total: allCategoryResults?.categories[category].total,
      total_results: allCategoryResults?.total,
    })

    setSearchParams({ query, filter: category })
  }

  const onResultSelect = useCallback(async (result: QuickSearchResult) => {
    const category = getCategoryFromResultType(result.type)
    const categoryResults: QuickSearchResult[] = isAllView
      ? allCategoryResults?.categories[category].results ?? []
      : categoryData?.results ?? []
    const rankPosition = categoryResults.findIndex(
      item => item.id === result.id && item.type === result.type,
    ) + 1

    trackAnalyticsEvent(ANALYTICS_EVENTS.QUICK_SEARCH_RESULT_CLICKED, {
      journey_channel: ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH,
      source_location: 'quick_search_results_page',
      season,
      query,
      query_length: query.length,
      selected_view: selectedView,
      result_category: category,
      result_type: result.type,
      result_id: result.id,
      rank_position: rankPosition > 0 ? rankPosition : undefined,
      category_total: isAllView
        ? allCategoryResults?.categories[category].total
        : categoryData?.total,
      total_results: isAllView && allCategoryResults ? allCategoryResults.total : undefined,
    })

    setPendingJourneyChannel(ANALYTICS_JOURNEY_CHANNELS.QUICK_SEARCH)
    await navigate(buildQuickSearchResultLocation(result, season))
  }, [allCategoryResults, categoryData, isAllView, navigate, query, season, selectedView])

  return { handleFilterChange, handleSeeAll, onResultSelect }
}
